import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Button from "components/Button/Regular";
import Input from "components/Input/Input";
import Select from "components/Input/Select";
import vgToast from "utils/toast";
import { addCustomLine } from "services/invoices.service";
import { BookingConfig } from "services/booking-configs.service";

type Props = {
  open: boolean;
  orgId: number;
  invoiceId: number;
  contactId?: number;
  currency?: number;
  /** The configurations a custom charge can hang off. */
  configs: BookingConfig[];
  onCancel: () => void;
  onAdded: () => void;
};

/**
 * A charge that is neither a booking nor a catalogue product: a callout fee, a
 * dressing, an agreed discount.
 *
 * The description is the reason this exists, so it is required. A negative amount
 * is allowed on purpose - a discount is a line like any other, and expressing it
 * as one keeps the total honest instead of quietly adjusting another line.
 */
const AddCustomLine = ({
  open,
  orgId,
  invoiceId,
  contactId,
  currency,
  configs,
  onCancel,
  onAdded,
}: Props) => {
  const { getAccessTokenSilently } = useAuth0();

  const [description, setDescription] = React.useState("");
  const [amount, setAmount] = React.useState("");
  const [configId, setConfigId] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setDescription("");
    setAmount("");
    setConfigId(configs.length === 1 ? String(configs[0].serviceFulfilmentConfigId) : "");
    setError(null);
  }, [open, configs]);

  if (!open) return null;

  const chosen = configs.find(
    (c) => String(c.serviceFulfilmentConfigId) === configId
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!contactId) {
      setError("This invoice has no client to charge.");
      return;
    }
    if (!chosen) {
      setError("Choose what this charge is recorded against.");
      return;
    }
    if (!description.trim()) {
      setError("Describe the charge - it is what the client reads on the bill.");
      return;
    }
    if (!Number.isFinite(Number(amount)) || amount === "") {
      setError("Enter an amount.");
      return;
    }

    setError(null);
    setBusy(true);
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await addCustomLine(token, {
        orgId,
        invoiceId,
        contactId,
        serviceId: chosen.serviceId,
        serviceFulfilmentConfigId: chosen.serviceFulfilmentConfigId,
        description: description.trim(),
        amount: Number(amount),
        currency: currency ?? 0,
      });

      if (apiError) {
        setError(apiError.message);
        return;
      }

      vgToast.success("Charge added.");
      onAdded();
    } catch (err) {
      vgToast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 print:hidden">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-slate-900/35 backdrop-blur-[2px]"
        onClick={() => {
          if (!busy) onCancel();
        }}
      />

      <form
        onSubmit={submit}
        className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-3xl border border-slate-200 bg-white shadow-[0_18px_50px_-12px_rgba(15,23,42,0.28)]"
      >
        <div className="border-b border-slate-100 bg-slate-50/70 px-6 py-5">
          <h3 className="text-lg font-semibold text-slate-900">Add a charge</h3>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            Something that is not a booking or a product - a callout fee, or a
            discount.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 px-6 py-5 sm:grid-cols-2">
          {configs.length === 0 && (
            <p className="sm:col-span-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              There are no active service configurations to record a charge
              against.
            </p>
          )}

          <div className="sm:col-span-2">
            <Input
              type="text"
              label="Description"
              placeholder="Callout fee"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <Input
            type="number"
            label="Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />

          <Select
            label="Recorded against"
            placeholder="Choose a service"
            options={configs.map((c) => ({
              title: c.name || `Configuration ${c.serviceFulfilmentConfigId}`,
              value: String(c.serviceFulfilmentConfigId),
            }))}
            value={configId}
            onChange={(e) => setConfigId(e.target.value)}
          />

          <p className="sm:col-span-2 text-xs text-slate-500">
            A negative amount records a discount.
          </p>

          {error && <p className="sm:col-span-2 text-sm text-red-500">{error}</p>}
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <Button color="Secondary" onClick={onCancel} disabled={busy}>
            <span>Cancel</span>
          </Button>
          <Button
            color="Primary"
            type="submit"
            disabled={busy || configs.length === 0}
          >
            <span>{busy ? "Adding…" : "Add charge"}</span>
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddCustomLine;
